/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

"use strict";

import * as $ from "jquery";
import {WebWorkerTeamAppsConnection} from "./WebWorkerTeamAppsConnection";
import {generateUUID, getIconPath, logException} from "./Common";
import {TeamAppsUiContext, TeamAppsUiEventDispatcher} from "./TeamAppsUiContext";
import {UiConfigurationConfig} from "../generated/UiConfigurationConfig";
import {UiWeekDay} from "../generated/UiWeekDay";
import {UiComponent} from "./UiComponent";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiEvent} from "../generated/UiEvent";
import {UiRootPanel} from "./UiRootPanel";
import {EventRegistrator} from "../generated/EventRegistrator";
import {UiCommand} from '../generated/UiCommand';
import {CommandExecutor} from "../generated/CommandExecutor";
import {TeamAppsConnection, TeamAppsConnectionListener} from "../shared/TeamAppsConnection";
import * as log from "loglevel";
import * as jstz from "jstz";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {TemplateRegistry} from "./TemplateRegistry";
import {createUiClientInfoConfig, UiClientInfoConfig} from "../generated/UiClientInfoConfig";
import {UiGenericErrorMessageOption} from "../generated/UiGenericErrorMessageOption";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class DefaultTeamAppsUiContext implements TeamAppsUiContext, TeamAppsUiEventDispatcher {

	private static logger = log.getLogger("DefaultTeamAppsUiContext");

	public readonly sessionId: string;
	public isHighDensityScreen: boolean;
	public config: UiConfigurationConfig = {
		_type: "UiConfigurationConfig",
		isoLanguage: "en",
		theme: null,
		optimizedForTouch: false,
		iconPath: "icons/icons-o",
		timeZoneId: "Europe/Berlin",
		firstDayOfWeek: UiWeekDay.MONDAY,
		dateFormat: "yyyy-MM-dd",
		timeFormat: "HH:mm",
		decimalSeparator: ".",
		thousandsSeparator: ""
	};
	public readonly templateRegistry: TemplateRegistry = new TemplateRegistry(this);

	private components: { [identifier: string]: UiComponent<UiComponentConfig> } = {}; // only for debugging!!!
	private _executingCommand: boolean = false;
	private connection: TeamAppsConnection;

	constructor(options: {
		webSocketUrl?: string,
		connectionClass?: {
			new (webSocketUrl: string, sessionId: string, clientInfo: UiClientInfoConfig, commandHandler: TeamAppsConnectionListener): TeamAppsConnection
		}
	}) {
		this.sessionId = generateUUID();

		let clientInfo = createUiClientInfoConfig({
			viewPortWidth: window.innerWidth,
			viewPortHeight: window.innerHeight,
			screenWidth: window.screen.width,
			screenHeight: window.screen.height,
			highDensityScreen: this.isHighDensityScreen,
			timezoneOffsetMinutes: new Date().getTimezoneOffset(),
			timezoneIana: jstz.determine().name(),
			clientTokens: UiRootPanel.getClientTokens()
		});

		let webSocketUrl = options.webSocketUrl;
		if (webSocketUrl == null) {
			let port = (window.location.port || (window.location.protocol === "https:" ? "443" : "80"));
			webSocketUrl = (window.location.protocol === "https:" ? "wss:" : "ws:") + "//" + window.location.hostname + ':' + port + "/communication";
		}
		
		let connectionListener: TeamAppsConnectionListener = {
			onConnectionInitialized: () => {
			},
			onConnectionBroken: (reason, message) => {
				DefaultTeamAppsUiContext.logger.error("Connection broken.");
				sessionStorage.clear();
				UiRootPanel.showGenericErrorMessage("Server-side Error", "A server error has occurred! You might experience unexpected behavior until you reload this web page.",
					[UiGenericErrorMessageOption.OK, UiGenericErrorMessageOption.RELOAD], this);
			},
			onCommand: (uiCommand: UiCommand) => this.executeCommand(uiCommand),
			onCommands: (uiCommands: UiCommand[], singleAnimationFrame: boolean) => this.executeCommands(uiCommands, singleAnimationFrame)
		};

		let connectionClass = options.connectionClass || WebWorkerTeamAppsConnection;
		this.connection = new connectionClass(webSocketUrl, this.sessionId, clientInfo, connectionListener);

		this.isHighDensityScreen = ((window.matchMedia && (window.matchMedia('only screen and (min-resolution: 124dpi), only screen and (min-resolution: 1.3dppx), only screen and (min-resolution: 48.8dpcm)').matches || window.matchMedia('only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (min-device-pixel-ratio: 1.3)').matches)) || (window.devicePixelRatio && window.devicePixelRatio > 1.3));
		if (this.isHighDensityScreen) {
			$(document.body).addClass('high-density-screen');
		}
	}

	public get executingCommand() {
		return this._executingCommand;
	}

	public fireEvent(eventObject: UiEvent, redundancyKey?: string) {
		this.connection.sendEvent(eventObject, redundancyKey);
	}

	registerComponent(component: UiComponent<UiComponentConfig>): void {
		this.components[component.getId()] = component;
		EventRegistrator.registerForEvents(component, component.getTeamAppsType(), eventObject => {
			this.fireEvent(eventObject);
		});
	}

	public createComponent(config: UiComponentConfig) {
		let component: UiComponent<UiComponentConfig>;
		if (TeamAppsUiComponentRegistry.getComponentClassForName(config._type)) {
			component = new (TeamAppsUiComponentRegistry.getComponentClassForName(config._type))(config, this);
			this.registerComponent(component);
			return component;
		} else {
			DefaultTeamAppsUiContext.logger.error("Unknown component type: " + config._type);
			return;
		}
	}

	public getComponentById(id: string): UiComponent<UiComponentConfig> {
		return this.components[id];
	}

	public getIconPath(iconName: string, iconSize: number, ignoreRetina?: boolean): string {
		return getIconPath(this, iconName, iconSize, ignoreRetina);
	}

	private executeCommands(uiCommands: UiCommand[], singleAnimationFrame: boolean) {
		let executeCommands = () => uiCommands.forEach(c => {
			try {
				this.executeCommand(c);
			} catch (error) {
				logException(error);
			}
		});
		if (singleAnimationFrame) {
			requestAnimationFrame(() => {
				executeCommands();
			});
		} else {
			executeCommands();
		}
	}

	private executeCommand(command: UiCommand) {
		try {
			this._executingCommand = true;
			const commandMethodName = command._type.substring(command._type.lastIndexOf('.') + 1);
			if (command.componentId) {
				DefaultTeamAppsUiContext.logger.trace(`Trying to call ${command.componentId}.${commandMethodName}()`);
				let component: any = this.components[command.componentId];
				if (!component) {
					throw new Error("The component " + command.componentId + " does not exist, so cannot call " + commandMethodName + "() on it.");
				} else if (typeof component[commandMethodName] !== "function") {
					throw new Error(`The ${(<any>component.constructor).name || 'component'} ${command.componentId} does not have a method ${commandMethodName}()!`);
				}
				let startTime = new Date().getTime();
				CommandExecutor.executeCommand(component, command);
				DefaultTeamAppsUiContext.logger.debug("... took " + (new Date().getTime() - startTime) + "ms");
			} else {
				DefaultTeamAppsUiContext.logger.trace(`Trying to call static method ${command._type}()`);
				CommandExecutor.executeStaticCommand(command, this);
			}
		} catch (e) {
			logException(e);
		} finally {
			this._executingCommand = false;
		}
	}
}
